import {
  Box,
  chakra,
  Flex,
  Grid,
  Icon,
  Image,
  shouldForwardProp,
  Text,
  useColorModeValue
} from '@chakra-ui/react';
import { isValidMotionProp, motion } from 'framer-motion';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { MdHistory } from 'react-icons/md';
import { PiChartLineUpBold } from 'react-icons/pi';
import { TbPigMoney, TbReportAnalytics } from 'react-icons/tb';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { gameListFromBottom } from '../../../animateConstant';
import { clearCashRegister } from '../../../api/postApi';
import Balance from '../../../components/Balance/Balance';
import { CASH_FLOW_OPEN, COMMON_WEB_PATH } from '../../../constant';
import { openClearCash } from '../../../redux/action/member/memberAction';
import BalanceAmount from '@/components/Balance/BalanceAmount';
import { BiMoneyWithdraw } from 'react-icons/bi';

const isCredit = process.env.REACT_APP_CREDIT == 1;

const ChakraBox = chakra(motion.div, {
  shouldForwardProp: (prop) =>
    isValidMotionProp(prop) || shouldForwardProp(prop)
});

const common_tab = [
  {
    icon: TbPigMoney,
    text: "Deposit",
    link: "deposit",
    hidden: !CASH_FLOW_OPEN,
    useImage: true,
    image: {
      light: `${COMMON_WEB_PATH}/v4-layout/member-icon/deposit2.svg`,
      dark: `${COMMON_WEB_PATH}/v4-layout/member-icon/deposit3.svg`,
    },
  },
  {
    icon: BiMoneyWithdraw,
    text: "Withdraw",
    link: "withdraw",
    hidden: !CASH_FLOW_OPEN,
    useImage: true,
    image: {
      light: `${COMMON_WEB_PATH}/v4-layout/member-icon/withdraw2.svg`,
      dark: `${COMMON_WEB_PATH}/v4-layout/member-icon/withdraw3.svg`,
    },
  },
  // {
  //   //棄分
  //   icon: BiMoneyWithdraw,
  //   text: "Fold",
  //   isFoldAction: true, // 是否為棄分
  //   //link: "withdraw",

  //   useImage: true,
  //   image: {
  //     light: `${COMMON_WEB_PATH}/v4-layout/member-icon/withdraw2.svg`,
  //     dark: `${COMMON_WEB_PATH}/v4-layout/member-icon/withdraw3.svg`,
  //   },
  // },
  {
    icon: TbReportAnalytics,
    text: 'Bet Record',
    link: 'betRecord'
  },
  {
    icon: MdHistory,
    text: 'Transaction',
    link: 'transaction',
    useImage: true,
    image: {
      light: `${COMMON_WEB_PATH}/v4-layout/member-icon/records2.svg`,
      dark: `${COMMON_WEB_PATH}/v4-layout/member-icon/records3.svg`
    }
  },
  {
    icon: PiChartLineUpBold,
    text: 'Rebate',
    link: 'my-rebate',
    auth: true,
    hidden: !isCredit
  }
];

const CommonSelectTab = () => {
  const { t } = useTranslation();
  const i18n = (key) => t(`common.${key}`);
  const i18n_profile = (key) => t(`profile.${key}`);

  const cardBg = useColorModeValue('light.250', 'dark.250');
  const cardHoverBg = useColorModeValue('light.200', 'dark.300');
  const textColor = useColorModeValue('light.100', 'dark.100');
  const iconBg = useColorModeValue('#EFEFF5', 'dark.200');
  const iconColor = useColorModeValue('#111', '#EFEFF5');
  const currentTheme = useColorModeValue('light', 'dark');

  const memberData = useSelector((state) => state.isMemberInfo);
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  // 棄分用
  const handleClearCashRegister = async () => {
    try {
      await clearCashRegister();
    } catch (error) {
      console.error('棄分失敗:', error);
    }
  };

  const handleItemClick = (item) => {
    if (item.isFoldAction) {
      // 打開確認彈窗
      dispatch(openClearCash());
    }
  };

  const currentPath = useCallback(
    (target = '') => {
      const splitPathname = location.pathname.split('/');

      if (target) {
        return splitPathname.some((item) => item === target);
      }
    },
    [location.pathname]
  );

  // 根據不同條件過濾需要顯示的按鈕
  const filteredTabs = useMemo(() => {
    return common_tab.filter((item) => {
      // 處理棄分按鈕的顯示邏輯
      if (item.isFoldAction) {
        // 當settlement_model是cash時顯示 是credit時隱藏
        return memberData?.settlement_model === 'cash';
      }
      // 其他按鈕的原有隱藏邏輯
      return !item.hidden;
    });
  }, [memberData?.settlement_model]);

  return (
    <ChakraBox
      {...gameListFromBottom}
      gap="5px"
      as="section"
      borderRadius={`20px`}
      borderTopRadius={'40px'}
      overflow={'hidden'}
      bg="#083549"
    >
      <Flex
        bgImage={`${COMMON_WEB_PATH}/blue-layout/profile/main_balance_bg.png`}
        bgSize="100% 100%"
        bgPosition="center"
        bgRepeat="no-repeat"
        borderRadius={`20px`}
        p="25px 10px"
        justifyContent={'center'}
        alignItems={'center'}
      >
        <Flex
          color="light.100"
          alignItems={'center'}
          direction={`column`}
          gap="0px"
        >
          <Flex align={'center'} gap="5px">
            <Image
              w="30px"
              h="30px"
              src={`${COMMON_WEB_PATH}/blue-layout/profile/balance_icon.png`}
            />
            <Text
              dropShadow={'#6EEFFF,0px,0px'}
              fontSize="xl"
              fontWeight={`500`}
            >
              {i18n_profile('Balance')}
            </Text>
          </Flex>
          <Flex
            px="15px"
            py="10px"
            bgSize={'100% 100%'}
            bgPos="center"
            bgRepeat="no-repeat"
            bgImage={`${COMMON_WEB_PATH}/blue-layout/profile/balance_bg.png`}
            sx={{
              backgroundPositionY: '10px'
            }}
            minW="160px"
            justifyContent={'center'}
            alignItems={'center'}
          >
            <BalanceAmount />
          </Flex>
        </Flex>
      </Flex>

      <Grid
        gridTemplateColumns={`repeat(4, minmax(0, 1fr))`}
        gap="10px"
        overflow="hidden"
        px="20px"
        py="15px"
      >
        {filteredTabs.map((item) => {
          const active = currentPath(item.link);

          // 根據是否為棄分動作決定要渲染的元素
          const WrapperComponent = item.isFoldAction ? Box : Link;
          const wrapperProps = item.isFoldAction
            ? {
                onClick: () => handleItemClick(item),
                className: 'overflow-hidden',
                cursor: 'pointer'
              }
            : {
                className: 'overflow-hidden',
                to: `../profile/${item.link}`
              };

          return (
            <WrapperComponent key={item.text} {...wrapperProps}>
              <Flex
                role="group"
                direction={`column`}
                alignItems={`center`}
                gap="5px"
              >
                <Flex
                  direction={`column`}
                  gap="3px"
                  justifyContent={`center`}
                  alignItems={`center`}
                  w="45px"
                  h="45px"
                  borderRadius={`full`}
                  py="7px"
                  className="transition duration-100"
                >
                  {item.useImage ? (
                    // 如果有圖片，就顯示圖片
                    <Image
                      src={item.image[currentTheme]}
                      alt={item.text}
                      w="24px"
                      h="24px"
                      objectFit="contain"
                    />
                  ) : (
                    <Icon as={item.icon} fontSize={`2xl`} color={'#CCFFFF'} />
                  )}
                </Flex>
                <Text
                  fontSize="xs"
                  letterSpacing={`tight`}
                  w="100%"
                  textAlign={`center`}
                  fontWeight={active ? `700` : '400'}
                  color={'#CCFFFF'}
                >
                  {i18n(item.text?.toLowerCase())}
                </Text>
              </Flex>
            </WrapperComponent>
          );
        })}
      </Grid>
    </ChakraBox>
  );
};

export default CommonSelectTab;
